import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Checkbox, FormControlLabel, CircularProgress,
  Alert, Divider, Grid, Card, CardContent, TextField, Select, MenuItem, Chip,
  ToggleButton, ToggleButtonGroup, Tooltip, IconButton, SvgIconProps,
  FormControl, InputLabel
} from '@mui/material';
import {
  Save as SaveIcon, DashboardCustomize as DashboardIcon, Visibility as LiveIcon,
  ArrowUpward, ArrowDownward, PersonOutline, Badge as BadgeIcon, School as SchoolIcon,
  GroupWork as GroupsIcon, MeetingRoom as MeetingRoomIcon, Schedule as ScheduleIcon,
  Quiz as QuizIcon, AttachMoney as MoneyIcon, Receipt as ReceiptIcon, HowToReg as PreRegIcon,
  ThumbUp as AcceptedIcon, ThumbDown as RefusedIcon, Warning as WarningIcon,
  NotificationsActive as AlertIcon, TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon, Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon,
  ListAlt as ListAltIcon, Flag as FlagIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApplication } from '../contexts/ApplicationContext';
import blobticService from '../services/blobticService';
import { blobticMessage } from '../components/BlobticMessage';
import GenericPage from '../components/GenericPage';
import { ZoomField } from '../components/ZoomField';
import { ZoomData } from '../services/blobticZoomService';
import { useLanguage } from '../contexts/LanguageContext';

export interface KpiItemConfig {
  name: string;
  title: string;
  translationKey?: string;
  desc?: string;
  order: number;
  color?: string;
  icon?: string;
  enabled?: boolean;
  type?: string;
  span?: { sm: number; md: number; lg: number };
  widgetConfig?: {
    thresholds?: Array<{ min?: number; max?: number; color: string; borderColor: string; statusKey: string }>;
    currencyField?: boolean;
    route?: string;
    manageKey?: string;
    [key: string]: any;
  };
}

const ICON_MAP: Record<string, React.FC<SvgIconProps>> = {
  People: PersonOutline, Badge: BadgeIcon, School: SchoolIcon, Groups: GroupsIcon,
  MeetingRoom: MeetingRoomIcon, Schedule: ScheduleIcon, Quiz: QuizIcon,
  AttachMoney: MoneyIcon, Receipt: ReceiptIcon, HowToReg: PreRegIcon,
  ThumbUp: AcceptedIcon, ThumbDown: RefusedIcon, Warning: WarningIcon,
  NotificationImportant: AlertIcon,
  TrendingUp: TrendingUpIcon,
  MonetizationOn: MonetizationOnIcon,
  Assignment: AssignmentIcon,
  PlayArrow: PlayArrowIcon,
  CheckCircle: CheckCircleIcon,
  ListAlt: ListAltIcon,
  Flag: FlagIcon
};

const GRADIENT_MAP: Record<string, string> = {
  itIndigo: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
  itBlue: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
  itPurple: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
  itCyan: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  itEmerald: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  itTeal: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
  itViolet: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  itAmber: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  itOrange: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  itRed: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  itGreen: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
};

const WIDGET_MOCKS: Record<string, { titleKey: string; rows: number; accentColor: string }> = {
  todayPreRegistrations: { titleKey: 'admin.config_kpi.widget.today_prereg', rows: 3, accentColor: '#f59e0b' },
  todaysSchedule: { titleKey: 'admin.config_kpi.widget.today_schedule', rows: 4, accentColor: '#0284c7' },
  lowAttendanceAlerts: { titleKey: 'admin.config_kpi.widget.low_attendance', rows: 2, accentColor: '#ef4444' },
  totalOutstandingDebt: { titleKey: 'admin.config_kpi.widget.outstanding_debt', rows: 0, accentColor: '#ef4444' },
  recentLeads: { titleKey: 'crm.dashboard.widget.recent_leads', rows: 3, accentColor: '#0284c7' },
  pipelineStages: { titleKey: 'crm.dashboard.widget.pipeline_stages', rows: 3, accentColor: '#7c3aed' },
  recentActivities: { titleKey: 'crm.dashboard.widget.recent_activities', rows: 3, accentColor: '#f97316' },
  projectProgress: { titleKey: 'project.dashboard.widget.project_progress', rows: 4, accentColor: '#4f46e5' },
  urgentTasks: { titleKey: 'project.dashboard.widget.urgent_tasks', rows: 3, accentColor: '#ef4444' }
};

export const KpiConfigPage: React.FC = () => {
  const { user } = useAuth();
  const { currentApp } = useApplication();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryApp = searchParams.get('app');
  const currentAppName = typeof currentApp === 'string' ? currentApp : (currentApp as any)?.name;
  const initialApp = queryApp || currentAppName || 'Academy';
  const [selectedApp, setSelectedApp] = useState<string>(initialApp);

  useEffect(() => {
    const app = searchParams.get('app') || (typeof currentApp === 'string' ? currentApp : (currentApp as any)?.name);
    if (app && app !== selectedApp) {
      setSelectedApp(app);
    }
  }, [searchParams, currentApp, selectedApp]);

  // Remove dropdown application on CRM and ProjectManagement
  const isCrmOrProject = selectedApp === 'CRM' || selectedApp === 'ProjectManagement' ||
                         queryApp === 'CRM' || queryApp === 'ProjectManagement';

  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [availableKpis, setAvailableKpis] = useState<KpiItemConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [liveMode, setLiveMode] = useState(false);

  const spanOptions = useMemo(() => [
    { value: 3, label: t('admin.config_kpi.span.quarter') || '1/4 (3 col)' },
    { value: 4, label: t('admin.config_kpi.span.third') || '1/3 (4 col)' },
    { value: 6, label: t('admin.config_kpi.span.half') || '1/2 (6 col)' },
    { value: 8, label: t('admin.config_kpi.span.two_thirds') || '2/3 (8 col)' },
    { value: 12, label: t('admin.config_kpi.span.full') || 'Pleine largeur (12 col)' }
  ], [t]);

  useEffect(() => {
    if (!selectedProfile) { setAvailableKpis([]); return; }

    const loadKpiConfig = async () => {
      setLoading(true);
      try {
        let defaultList: KpiItemConfig[] = [];
        const defaultRes = await blobticService.executeZoomRequest<any>({
          ZoomKey: 'config_kpi',
          Action: { OKey: 'Blobtic_Sys_Profil,config_kpi,get_default_kpi', APP_NAME: selectedApp }
        });
        if (defaultRes.success && defaultRes.data) {
          const raw = Array.isArray(defaultRes.data) ? defaultRes.data[0] : defaultRes.data;
          if (raw?.kpiJson) defaultList = JSON.parse(raw.kpiJson);
        }

        const prfId = selectedProfile.PRF_IDF || selectedProfile.id || selectedProfile.value;
        const profilRes = await blobticService.executeZoomRequest<any>({
          ZoomKey: 'config_kpi',
          Action: { OKey: 'Blobtic_Sys_Profil,config_kpi,get_profil_kpi', PRF_IDF: prfId, APP_NAME: selectedApp }
        });
        let assignedList: KpiItemConfig[] = [];
        if (profilRes.success && profilRes.data) {
          const rawPrf = Array.isArray(profilRes.data) ? profilRes.data[0] : profilRes.data;
          if (rawPrf?.kpiJson) assignedList = JSON.parse(rawPrf.kpiJson);
        }

        if (assignedList.length > 0) {
          const byName = new Set(assignedList.map(k => k.name));
          setAvailableKpis(defaultList.map(k => ({ ...k, enabled: byName.has(k.name) })));
        } else {
          setAvailableKpis(defaultList.map(k => ({ ...k, enabled: true })));
        }
      } catch (err) {
        console.error('Failed to load KPI configuration:', err);
        blobticMessage.error(t('common.messages.error'), t('admin.config_kpi.load_error'));
      } finally {
        setLoading(false);
      }
    };
    loadKpiConfig();
  }, [selectedProfile, selectedApp, t]);

  const handleToggleKpi = (name: string) => {
    setAvailableKpis(prev => prev.map(k => k.name === name ? { ...k, enabled: !k.enabled } : k));
  };

  const handleToggleAll = (enableAll: boolean) => {
    setAvailableKpis(prev => prev.map(k => ({ ...k, enabled: enableAll })));
  };

  const handleOrderChange = (name: string, order: number) => {
    setAvailableKpis(prev => prev.map(k => k.name === name ? { ...k, order: Math.max(0, order) } : k));
  };

  const handleSpanChange = (name: string, breakpoint: 'sm' | 'md' | 'lg', value: number) => {
    setAvailableKpis(prev => prev.map(k => {
      if (k.name !== name) return k;
      return {
        ...k,
        span: {
          sm: k.span?.sm ?? 6,
          md: k.span?.md ?? 3,
          lg: k.span?.lg ?? 3,
          [breakpoint]: value
        }
      };
    }));
  };

  const sortedKpis = useMemo(() =>
    [...availableKpis].sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
    [availableKpis]
  );

  const enabledSorted = useMemo(() =>
    sortedKpis.filter(k => k.enabled),
    [sortedKpis]
  );

  const getPreviewIcon = (kpi: KpiItemConfig) => {
    const IconComp = ICON_MAP[kpi.icon || ''] || PersonOutline;
    return <IconComp sx={{ opacity: 0.8 }} />;
  };

  const getPreviewBg = (kpi: KpiItemConfig) =>
    GRADIENT_MAP[(kpi.color || '').replace(/-/g, '')] || GRADIENT_MAP.itIndigo;

  const getPreviewVal = (kpi: KpiItemConfig) => {
    if (kpi.name === 'totalRevenue') return '$4 530';
    if (kpi.type === 'widget') return '';
    const fakeVals: Record<string, string> = {
      // Academy
      totalActiveStudents: '142', totalTeachers: '12', totalCourses: '8', totalGroups: '16',
      totalRooms: '10', totalSessions: '23', totalQuizzes: '5', pendingInvoices: '7',
      pendingPreRegistrations: '4', acceptedPreRegistrations: '31', refusedPreRegistrations: '2',
      // CRM
      totalLeads: '48', newLeads: '14', totalContacts: '92', totalOpportunities: '26',
      pipelineAmount: '185 000 €', wonAmount: '64 500 €', pendingActivities: '11',
      // ProjectManagement
      totalProjects: '18', activeProjects: '7', totalTasks: '142', doneTasks: '89',
      overdueTasks: '6', totalBudget: '320 000 €', totalSpent: '145 200 €', pendingMilestones: '5'
    };
    return fakeVals[kpi.name] || '—';
  };

  const getSpanProps = (item: KpiItemConfig) => {
    const dft = item.type === 'widget' ? { sm: 12, md: 12, lg: 12 } : { sm: 6, md: 3, lg: 3 };
    return { xs: 12, sm: item.span?.sm ?? dft.sm, md: item.span?.md ?? dft.md, lg: item.span?.lg ?? dft.lg } as const;
  };

  const handleSave = async () => {
    if (!selectedProfile) { blobticMessage.error(t('common.messages.warning'), t('admin.config_kpi.select_profile_required')); return; }
    setSaving(true);
    try {
      const prfId = selectedProfile.PRF_IDF || selectedProfile.id || selectedProfile.value;
      const enabledKpis = availableKpis.filter(k => k.enabled);
      const res = await blobticService.executeZoomRequest<any>({
        ZoomKey: 'config_kpi',
        Action: {
          OKey: 'Blobtic_Sys_Profil,config_kpi,save_profil_kpi',
          PRF_IDF: prfId,
          APP_NAME: selectedApp,
          PRFKPI_KPI_JSON: JSON.stringify(enabledKpis)
        }
      });
      if (res.success) blobticMessage.success(t('common.messages.success'), t('admin.config_kpi.save_success'));
      else blobticMessage.error(t('common.messages.error'), res.message || t('admin.config_kpi.save_error'));
    } catch (err) {
      console.error('Failed to save KPI configuration:', err);
      blobticMessage.error(t('common.messages.error'), t('admin.config_kpi.save_server_error'));
    } finally { setSaving(false); }
  };

  const handleProfileChange = (val: string, data?: ZoomData) => {
    if (data) setSelectedProfile(data.rawData || data);
    else if (!val) setSelectedProfile(null);
  };

  const enabledCount = availableKpis.filter(k => k.enabled).length;

  const pageTitle = selectedApp === 'CRM'
    ? `${t('admin.config_kpi.title', 'Configuration des KPIs')} — CRM`
    : selectedApp === 'ProjectManagement'
    ? `${t('admin.config_kpi.title', 'Configuration des KPIs')} — ${t('project.menu.dashboard', 'Gestion de Projet')}`
    : t('admin.config_kpi.title', 'Configuration des KPIs');

  return (
    <GenericPage
      title={pageTitle}
      additionalActions={
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          {isCrmOrProject && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(selectedApp === 'CRM' ? '/CRM/dashboard' : '/ProjectManagement/dashboard')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {selectedApp === 'CRM' ? t('crm.menu.dashboard', 'Tableau de bord CRM') : t('project.menu.dashboard', 'Tableau de bord Projets')}
            </Button>
          )}
          <Button variant="contained" color="primary" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={!selectedProfile || loading || saving}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            {saving ? t('admin.config_kpi.saving') : t('admin.config_kpi.save')}
          </Button>
        </Box>
      }
    >
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          {!isCrmOrProject && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="app-select-label">{t('admin.config_kpi.select_app') || 'Application'}</InputLabel>
                <Select
                  labelId="app-select-label"
                  value={selectedApp}
                  label={t('admin.config_kpi.select_app') || 'Application'}
                  onChange={(e) => {
                    const newApp = e.target.value;
                    setSelectedApp(newApp);
                    setSearchParams({ app: newApp });
                  }}
                >
                  <MenuItem value="Academy">Academy (Académie & Formations)</MenuItem>
                  <MenuItem value="CRM">CRM (Gestion Relation Client)</MenuItem>
                  <MenuItem value="ProjectManagement">Gestion de Projet (Projects & Tasks)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={isCrmOrProject ? 8 : 6} md={isCrmOrProject ? 6 : 5}>
            <ZoomField
              zoomKey="profil"
              label={t('admin.config_kpi.select_profile')}
              placeholder={t('admin.config_kpi.search_profile')}
              value={selectedProfile ? (selectedProfile.PRF_CODE || selectedProfile.code || '') : ''}
              displayField="PRF_CODE"
              valueField="PRF_IDF"
              onChange={handleProfileChange}
              showSelectedInfo
              helperText={selectedProfile ? (selectedProfile.PRF_LIB || selectedProfile.label || '') : ''}
              required
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedProfile && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
              <DashboardIcon color="primary" />
              {t('admin.config_kpi.kpi_count', { app: selectedApp, enabled: enabledCount, total: availableKpis.length })}
            </Typography>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup value={liveMode ? 'live' : 'config'} exclusive size="small"
                onChange={(_, v) => setLiveMode(v === 'live')}>
                <ToggleButton value="config">{t('admin.config_kpi.mode_config')}</ToggleButton>
                <ToggleButton value="live"><LiveIcon fontSize="small" sx={{ mr: 0.5 }} />{t('admin.config_kpi.mode_live')}</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" variant="outlined" onClick={() => handleToggleAll(true)}>{t('admin.config_kpi.select_all')}</Button>
              <Button size="small" variant="outlined" color="secondary" onClick={() => handleToggleAll(false)}>{t('admin.config_kpi.deselect_all')}</Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress size={36} /></Box>
          ) : availableKpis.length === 0 ? (
            <Alert severity="info">{t('admin.config_kpi.no_kpi')}</Alert>
          ) : liveMode ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>{t('admin.config_kpi.live_preview')}</Alert>
              <Grid container spacing={2.5}>
                {enabledSorted.map(kpi => (
                  <Grid item {...getSpanProps(kpi)} key={kpi.name}>
                    {kpi.type === 'widget' ? (
                      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%', borderLeft: `8px solid ${WIDGET_MOCKS[kpi.name]?.accentColor || '#999'}` }}>
                        <Typography variant="h6" fontWeight="bold" mb={1} color="text.primary">
                          {t(WIDGET_MOCKS[kpi.name]?.titleKey || '', kpi.title)}
                          <Chip label="widget" size="small" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                        </Typography>
                        {Array.from({ length: WIDGET_MOCKS[kpi.name]?.rows || 2 }).map((_, i) => (
                          <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={`${9 + i}:00 - ${10 + i}:00`} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                            <Box sx={{ flex: 1, height: 8, bgcolor: 'action.hover', borderRadius: 2 }} />
                          </Paper>
                        ))}
                      </Paper>
                    ) : (
                      <Card sx={{ background: getPreviewBg(kpi), color: 'white', borderRadius: 3, height: '100%' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 'bold' }}>{kpi.title}</Typography>
                            {getPreviewIcon(kpi)}
                          </Box>
                          <Typography variant="h3" fontWeight="bold" my={1}>{getPreviewVal(kpi)}</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Grid container spacing={2}>
              {sortedKpis.map(kpi => (
                <Grid item xs={12} sm={6} md={4} key={kpi.name}>
                  <Card variant="outlined"
                    sx={{
                      borderColor: kpi.enabled ? 'primary.main' : 'divider',
                      bgcolor: kpi.enabled ? 'action.hover' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': { boxShadow: 2 }
                    }}>
                    <CardContent sx={{ p: '12px 16px !important' }}>
                      <FormControlLabel
                        control={<Checkbox checked={Boolean(kpi.enabled)} onChange={() => handleToggleKpi(kpi.name)} color="primary" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t(kpi.translationKey || '', kpi.title)}
                            </Typography>
                            <Chip
                              label={kpi.type === 'widget' ? t('admin.config_kpi.type_widget') : t('admin.config_kpi.type_card')}
                              size="small"
                              color={kpi.type === 'widget' ? 'secondary' : 'primary'}
                              sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                            />
                          </Box>
                        }
                      />
                      {kpi.desc && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                          {t(kpi.desc, kpi.desc)}
                        </Typography>
                      )}
                      <Box display="flex" alignItems="center" gap={1} mt={1.5} ml={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 50 }}>{t('admin.config_kpi.order')}</Typography>
                        <TextField type="number" size="small" value={kpi.order ?? 0} onChange={e => handleOrderChange(kpi.name, parseInt(e.target.value) || 0)}
                          sx={{ width: 80 }} inputProps={{ min: 0, style: { textAlign: 'center', padding: '6px 4px', fontSize: 13 } }} />
                        <IconButton size="small" onClick={() => handleOrderChange(kpi.name, (kpi.order ?? 0) - 1)} disabled={kpi.order === 0}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOrderChange(kpi.name, (kpi.order ?? 0) + 1)}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} mt={1} ml={6} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary" sx={{ width: 50 }}>{t('admin.config_kpi.width')}</Typography>
                        {(['sm', 'md', 'lg'] as const).map(bp => (
                          <Tooltip key={bp} title={`@${bp.toUpperCase()}`}>
                            <Select size="small" value={kpi.span?.[bp] ?? (kpi.type === 'widget' ? 12 : (bp === 'sm' ? 6 : 3))}
                              onChange={e => handleSpanChange(kpi.name, bp, e.target.value as number)}
                              sx={{ width: 120, fontSize: 13 }}>
                              {spanOptions.map(o => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{bp.toUpperCase()}: {o.label}</MenuItem>)}
                            </Select>
                          </Tooltip>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}
    </GenericPage>
  );
};
